import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Table } from 'antd';
import { Banknote, CalendarCheck, Download, Trophy, Wallet } from 'lucide-react';

import { useBranches, useCheckout } from '../../hooks';
import {
  BranchSelect,
  Card,
  ChartSkeleton,
  DataTable,
  DateRangeFilter,
  EmptyState,
  ErrorState,
  KpiCard,
  PageHeader,
} from '../../components';
import { TrendChart } from '../../components/Charts';
import { useI18n } from '../../i18n';
import { useTheme } from '../../theme';
import {
  enumerateDays,
  exportCsv,
  formatDate,
  formatMoney,
  formatNumber,
  formatPercent,
  getPresetRange,
} from '../../utils';

export const Checkout = () => {
  const { id } = useParams();
  const { t } = useI18n();
  const { palette } = useTheme();
  const [selectedBranch, setSelectedBranch] = useState(id ? +id : 0);
  const [period, setPeriod] = useState(() => ({ preset: 'last7', range: getPresetRange('last7') }));

  const params = useMemo(
    () => ({ branch_id: selectedBranch, start_date: period.range[0], end_date: period.range[1] }),
    [selectedBranch, period],
  );

  const { data: branches, isLoading: isLoadingBranches } = useBranches();
  const { data, isLoading, isError, refetch } = useCheckout(params, { keepPreviousData: true });

  const selectedBranchName = useMemo(
    () => branches?.find(branch => branch.id === selectedBranch)?.name ?? t('filters.allBranches'),
    [branches, selectedBranch, t],
  );

  const rows = useMemo(
    () =>
      (Array.isArray(data) ? data : []).map(item => ({
        id: item.report_date,
        date: item.report_date,
        amount: +item.total_amount || 0,
      })),
    [data],
  );

  const totalCheckout = useMemo(() => rows.reduce((acc, item) => acc + item.amount, 0), [rows]);
  const bestDay = useMemo(
    () => rows.reduce((best, row) => (!best || row.amount > best.amount ? row : best), null),
    [rows],
  );
  const days = useMemo(() => enumerateDays(period.range), [period.range]);

  const chartData = useMemo(() => {
    const byDate = Object.fromEntries(rows.map(r => [r.date, r.amount]));
    return days.map(date => ({ date, value: byDate[date] ?? 0 }));
  }, [rows, days]);

  const columns = [
    {
      title: t('tables.date'),
      dataIndex: 'date',
      render: date => <span className='wm-strong'>{formatDate(date, 'D MMMM YYYY, dddd')}</span>,
      sorter: (a, b) => a.date.localeCompare(b.date),
      defaultSortOrder: 'descend',
    },
    {
      title: t('charts.revenue'),
      dataIndex: 'amount',
      align: 'right',
      render: amount => <span className='wm-strong'>{formatMoney(amount)}</span>,
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: t('charts.share'),
      dataIndex: 'amount',
      key: 'share',
      align: 'right',
      width: 220,
      render: amount => {
        const share = totalCheckout ? amount / totalCheckout : 0;
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
            <span className='wm-rank__bar' style={{ width: 90, display: 'inline-block' }}>
              <i style={{ width: `${share * 100}%` }} />
            </span>
            <span className='wm-muted num' style={{ minWidth: 48 }}>
              {formatPercent(share)}
            </span>
          </span>
        );
      },
    },
  ];

  const exportRows = () =>
    exportCsv(
      `kassa-${period.range[0]}_${period.range[1]}`,
      [t('tables.date'), t('charts.revenue')],
      rows.map(r => [r.date, r.amount]),
    );

  return (
    <>
      <PageHeader
        title={t('checkout.title')}
        subtitle={t('checkout.subtitle', { branch: selectedBranchName })}
        actions={
          <Button icon={<Download size={15} />} onClick={exportRows} disabled={!rows.length}>
            {t('common.exportCsv')}
          </Button>
        }
      />

      <div className='wm-filter-bar'>
        <DateRangeFilter value={period} onChange={setPeriod} />
        <BranchSelect
          branches={branches}
          value={selectedBranch}
          onChange={setSelectedBranch}
          loading={isLoadingBranches}
        />
      </div>

      <div className='wm-grid wm-grid--kpi'>
        <KpiCard
          label={t('checkout.total')}
          icon={Wallet}
          value={totalCheckout}
          format={formatNumber}
          unit={t('common.currency')}
          loading={isLoading}
        />
        <KpiCard
          label={t('checkout.avgDay')}
          icon={Banknote}
          tone='neutral'
          value={days.length ? totalCheckout / days.length : 0}
          format={formatNumber}
          unit={t('common.currency')}
          loading={isLoading}
        />
        <KpiCard
          label={t('checkout.bestDay')}
          icon={Trophy}
          tone='success'
          value={bestDay ? bestDay.amount : null}
          format={formatNumber}
          unit={t('common.currency')}
          hint={bestDay ? formatDate(bestDay.date) : undefined}
          loading={isLoading}
        />
        <KpiCard
          label={t('checkout.days')}
          icon={CalendarCheck}
          tone='neutral'
          value={rows.filter(r => r.amount > 0).length}
          format={formatNumber}
          hint={`/ ${days.length} ${t('common.days')}`}
          loading={isLoading}
        />
      </div>

      <Card title={t('checkout.daily')} subtitle={t('checkout.dailySub')} className='mb-16'>
        {isLoading ? (
          <ChartSkeleton height={280} />
        ) : isError ? (
          <ErrorState onRetry={refetch} />
        ) : rows.length === 0 ? (
          <EmptyState title={t('states.emptyPeriod')} text={t('states.emptyHint')} />
        ) : (
          <TrendChart
            data={chartData}
            showPrevious={false}
            valueFormatter={formatMoney}
            currentLabel={t('charts.revenue')}
            color={palette.chart[0]}
            height={280}
          />
        )}
      </Card>

      <DataTable
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        columns={columns}
        dataSource={rows}
        pageSize={31}
        emptyTitle={t('states.emptyPeriod')}
        emptyText={t('states.emptyHint')}
        summary={() =>
          rows.length > 0 ? (
            <Table.Summary fixed='bottom'>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>{t('common.total')}</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align='right'>
                  {formatMoney(totalCheckout)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align='right'>
                  {formatPercent(1)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          ) : null
        }
      />
    </>
  );
};
